import { Descriptions } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CgCloseR } from 'react-icons/cg';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import {
  createAnswer,
  getInquiry,
  updateAnswer,
} from '../../../api/boardCommunication.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import FileUpload from '../../../components/FileUpload';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { getCommonCodeLabel } from '../../../lib/store';
import { IGetInquiry } from '../../../models/boardCommunication.model';
import { RootState } from '../../../store/rootReducer';

const Block = styled.div`
  .post-info {
    display: flex;
    margin-top: 30px;

    color: black;
  }

  .post-file {
    text-decoration: underline;
  }

  .table th {
    vertical-align: middle;
  }

  .table th,
  .table td {
    border: 1px solid #e8e8e8;
  }
  
  table textarea {padding-left:3px;}
`;

interface IMatch {
  communicationCode: string;
}

const BoardInquiryRead = () => {
  const { writeAuthYn } = useAuth();
  const match = useRouteMatch<IMatch>();
  const communicationCode = Number(match.params.communicationCode);
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetInquiry>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();
  const { campusData } = useSelector((state: RootState) => state.campusData);

  const [isAnswerMode, setIsAnswerMode] = useState(false);
  const [answer, setAnswer] = useState('');
  const [files, setFiles] = useState<any>([null]);
  const [fileinfo, setFileinfo] = useState<any>([]);

  useEffect(() => {
    if (communicationCode) {
      execute(getInquiry(communicationCode));
    }
  }, [communicationCode]);

  useEffect(() => {
    if (submitRes && !isLoading) {
      execute(getInquiry(communicationCode));
      successNotification(t('common.message.save1'));
      setIsAnswerMode(false);
    }
  }, [submitRes, submitIsLoading]);

  const getCampusName = (value: any) => {
    const campus = campusData?.find((item: any) => item.campusCode === value);
    return campus?.campusKorName || value;
  };

  const onEditAnswer = () => {
    if (!res?.data) return;

    if (res.data.answerMemCode) {
      setAnswer(res.data.answerString);
      setFileinfo(_.cloneDeep(res.data.answerFiles));
    }
    setIsAnswerMode(true);
  };

  const onSetFile = (index: number, file: any) => {
    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  };

  const onAddFile = () => {
    const _files = [...files];
    _files.push(null);
    setFiles(_files);
  };

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  };

  const onDeleteAttachedFile = (index: number) => {
    const _fileinfo = [...fileinfo];
    _fileinfo[index].originalName = null;
    setFileinfo(_fileinfo);
  };

  const onSubmitAnswer = () => {
    if (!answer) {
      return infoNotification(t('board.inquiry.message1'));
    }

    const form = new FormData();
    form.append('answerString', answer);

    if (res?.data?.answerMemCode) {
      // 수정 시 파일 처리
      if (res?.data?.answerFileCode) {
        form.append('answerFileCode', res?.data?.answerFileCode);
      }

      if (fileinfo) {
        // 기존 첨부파일 있음
        let hasNewFiles = false;
        files.forEach((item: any) => {
          if (item) hasNewFiles = true;
        });

        let isAllDeleted = true;
        let hasDeleted = false;
        fileinfo.forEach((item: any) => {
          if (item.originalName) {
            isAllDeleted = false;
          } else {
            hasDeleted = true;
            form.append('answerDeleteFiles', item.fileSeq);
          }
        });

        if (hasNewFiles) form.append('answerFileStatus', 'update');
        else if (isAllDeleted) form.append('answerFileStatus', 'delete');
        else if (hasDeleted) form.append('answerFileStatus', 'update');
        else form.append('answerFileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (files.length > 0 && files[0] !== null) {
          // 추가 첨부파일 있음
          form.append('answerFileStatus', 'create');
        } else {
          // 추가 첨부파일 없음
          form.append('answerFileStatus', 'keep');
        }
      }

      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('answerNewFiles', item);
        });
      }
    } else {
      // 생성 시 파일 처리
      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('files', item);
        });
      }
    }

    res?.data?.answerMemCode
      ? submitExecute(updateAnswer(communicationCode, form))
      : submitExecute(createAnswer(communicationCode, form));
  };

  const renderFileAttach = () => {
    return (
      <>
        {files.length > 0
          ? files.map((file: any, index: number) => (
              <div className="flex mb-2 comm-gap" key={index}>
                <FileUpload
                  file={files[index]}
                  setFile={(file: any) => onSetFile(index, file)}
                />
                <div className="comm-btn__area">
                  <Button
                    className="btn-basic small gray"
                    onClick={() => onDeleteFile(index)}
                    text={t('common.button.delete')}
                  />
                </div>
              </div>
            ))
          : null}
        <div className="comm-btn__area">
          <Button
            className="btn-basic pink-line"
            onClick={onAddFile}
            text={t('common.button.add')}
          />
        </div>
      </>
    );
  };

  return (
    <Block className="content scrollbar">
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <SimpleHeader
        parentMenu={t('common.menu.board')}
        currentMenu={t('board.inquiry.title2')}
        iconFile="icon-board-big.svg"
      />
      
      <div className='comm-table__area rowType'>
      <Descriptions bordered column={5} size="small">
        <Descriptions.Item label={t('common.column.title')} span={5}>
          {res?.data?.title}
        </Descriptions.Item>
        <Descriptions.Item label={t('board.inquiry.column1')}>
          {getCommonCodeLabel(
            'BR0001',
            res?.data?.communicationDiv?.trim() || '',
            'ko',
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.column.status')}>
          {getCommonCodeLabel(
            'BR0002',
            res?.data?.communicationStatus || '',
            'ko',
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.menu.campus')}>
          {getCampusName(res?.data?.campusCode)}
        </Descriptions.Item>
        <Descriptions.Item label={t('archive.learn.column1')}>
          {res?.data?.questionMemKorName}
        </Descriptions.Item>
        <Descriptions.Item label={t('archive.learn.column2')}>
          {moment(res?.data?.questionDate).format('YYYY.MM.DD HH:MM:SS')}
        </Descriptions.Item>
        <Descriptions.Item label={t('board.inquiry.column3')} span={5}>
          <div
            dangerouslySetInnerHTML={{ __html: res?.data?.content || '' }}
          />
        </Descriptions.Item>
        {res?.data?.questionFiles ? (
          <Descriptions.Item label={t('archive.exam.column7')}>
            {res?.data?.questionFiles.map((item: any) => (
              <a
                className="btn-text"
                href={item.fileLocation}
                download={item.originalName}
              >
                {item.originalName}
              </a>
            ))}
          </Descriptions.Item>
        ) : null}
      </Descriptions>
      </div>

      {isAnswerMode ? (
        // 답변 등록/수정
        <div className='comm-table__area rowType mt-5'>
        <Descriptions bordered column={2} size="small" >
          <Descriptions.Item label={t('board.inquiry.column4')}>
            {res?.data?.answerMemCode
              ? res?.data?.answerMemKorName
              : auth?.korName}
          </Descriptions.Item>
          <Descriptions.Item label={t('board.inquiry.column5')}>
            {res?.data?.answerDate
              ? moment(res?.data?.answerDate).format('YYYY.MM.DD HH:MM:SS')
              : null}
          </Descriptions.Item>
          <Descriptions.Item label={t('board.inquiry.column6')} span={2}>
            <textarea
              className="w-100"
              rows={3}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
          </Descriptions.Item>
          {res?.data?.answerMemCode ? (
            // 첨부파일 수정
            <>
              {fileinfo ? (
                <Descriptions.Item label={t('archive.learn.column5')} span={2}>
                  {fileinfo.map((item: any, index: number) => {
                    const result = item?.originalName ? (
                      <div>
                        <a
                          className="btn-text"
                          href={`${item.fileLocation}`}
                          download={item.originalName}
                        >
                          {item.originalName}
                        </a>
                        <span
                          className="ml-1 pointer"
                          onClick={() => onDeleteAttachedFile(index)}
                        >
                          <CgCloseR />
                        </span>
                      </div>
                    ) : null;
                    return result;
                  })}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item
                label={fileinfo ? t('archive.learn.column6') : t('archive.exam.column7')}
                span={2}
              >
                {renderFileAttach()}
              </Descriptions.Item>
            </>
          ) : (
            // 첨부파일 신규
            <Descriptions.Item label={t('archive.exam.column7')} span={2}>
              {renderFileAttach()}
            </Descriptions.Item>
          )}
        </Descriptions>
        </div>
      ) : // 등록된 답변 보기
      res?.data?.answerMemCode ? (
        <div className='comm-table__area rowType mt-5'>
        <Descriptions
          bordered
          column={2}
          size="small"
          labelStyle={{ width: '25%' }}
          contentStyle={{ width: '25%' }}
        >
          <Descriptions.Item label={t('board.inquiry.column4')}>
            {res?.data?.answerMemKorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('board.inquiry.column5')}>
            {moment(res?.data?.answerDate).format('YYYY.MM.DD HH:MM:SS')}
          </Descriptions.Item>
          <Descriptions.Item label={t('board.inquiry.column6')} span={2}>
            <div style={{ whiteSpace: 'pre' }}>{res?.data?.answerString}</div>
          </Descriptions.Item>
          {res?.data?.answerFiles ? (
            <Descriptions.Item label={t('archive.exam.column7')} span={2}>
              {res?.data?.answerFiles.map((item: any) => (
                <a
                  className="btn-text"
                  href={item.fileLocation}
                  download={item.originalName}
                >
                  {item.originalName}
                </a>
              ))}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
        </div>
      ) : (
            <div className="text-center mt-3">{t('board.inquiry.message2')}</div>
      )}

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => history.push(`/board/inquiry${location.search}`)}
            text={t('common.button.list')}
          />
        </div>
        <div className="comm-btn__area right">
          {auth?.hqYn === 'Y' ? (
            isAnswerMode ? (
              <Button
                className="btn-basic pink"
                onClick={onSubmitAnswer}
                text={t('board.inquiry.button1')}
                writeAuthYn={writeAuthYn}
              />
            ) : (
              <Button
                className="btn-basic pink"
                onClick={onEditAnswer}
                text={res?.data?.answerMemCode ? t('board.inquiry.button2') : t('board.inquiry.button3')}
                writeAuthYn={writeAuthYn}
              />
            )
          ) : (
            <Button
              className="btn-basic pink-line"
              onClick={() =>
                history.push(
                  `/board/inquiry/${communicationCode}/edit${location.search}`,
                )
              }
              text={t('common.button.edit')}
              writeAuthYn={writeAuthYn}
            />
          )}
        </div>
      </div>
    </Block>
  );
};

export default BoardInquiryRead;
